import { LabHomeResults } from "./LabResultsOverview";
import { LabPreviewControls } from "./LabPreviewControls";
import { LabHowItWorks } from "./LabHowItWorks";
import {
  useLabsExperiment,
  useLabCardImpression,
  useLabEvent,
} from "@/features/labs/labExperiment";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { HomeFeatureCard } from "@/features/home/components/HomeFeatureCard";
import { useLabOrders } from "@/lib/api/labHooks";
import type { LabOrder } from "@/types/labs";
import {
  featuredLabOrder,
  labHomeCopy,
  labOrderLabel,
} from "./labPresentation";
import { LabKitProgress } from "./LabKitProgress";

const actionClass =
  "min-h-44 flex-1 flex justify-between items-center gap-10 px-15 py-15 font-body text-14 leading-130 text-white bg-black hover:bg-[#1a1a1a]";

export function LabHomeCard() {
  const { enabled: labsEnabled } = useLabsExperiment();
  const query = useLabOrders(labsEnabled);
  if (!labsEnabled) return null;
  return (
    <>
      <LabHomeCardContent
        order={featuredLabOrder(query.data?.orders ?? [])}
        loading={query.isLoading}
        unavailable={query.isError}
      />
      {!query.isError && <LabHomeResults orders={query.data?.orders ?? []} />}
    </>
  );
}

export function LabHomeCardContent({
  order,
  loading = false,
  unavailable = false,
}: {
  order?: LabOrder;
  loading?: boolean;
  unavailable?: boolean;
}) {
  const impressionRef = useLabCardImpression();
  const emit = useLabEvent();
  const copy = labHomeCopy(order);
  const title = loading
    ? "Your blood testing."
    : unavailable
      ? "Your Labs, in one place."
      : copy.title;
  const description = loading
    ? "Fetching your kit updates and reports."
    : unavailable
      ? "We couldn’t retrieve your latest update. Open Labs to try again."
      : copy.description;
  const destination = loading || unavailable ? "/app/labs" : copy.to;
  return (
    <div ref={impressionRef} className="ph-no-capture">
      <LabPreviewControls />
      <HomeFeatureCard
        label="Blood testing"
        meta={
          !loading && !unavailable && order ? labOrderLabel(order) : "At home"
        }
        actions={
          <>
            {!order && !loading && !unavailable && (
              <LabHowItWorks source="home_card" cardAction />
            )}
            <Link
              className={actionClass}
              to={destination}
              onClick={() =>
                emit("blood_test_callout_clicked", { source: "home_card" })
              }
            >
              {loading || unavailable ? "Open Labs" : copy.action}
              <ArrowRight size={16} aria-hidden />
            </Link>
            {order && !loading && !unavailable && (
              <Link
                className={`${actionClass} border-l border-white/25`}
                to="/app/labs"
                onClick={() =>
                  emit("blood_test_labs_clicked", { source: "home_card" })
                }
              >
                All lab tests
                <ArrowRight size={16} aria-hidden />
              </Link>
            )}
          </>
        }
      >
        <div className="px-15 py-20 flex items-center gap-15">
          <div className="min-w-0 flex-1">
            <h2 className="font-mono uppercase text-24 leading-110 text-white">
              {title}
            </h2>
            <p className="font-body text-15 text-white/75 mt-12 leading-140">
              {description}
            </p>
          </div>
          <img
            src="/images/tasso/system-collection-device-transparent.png"
            alt="System at-home blood collection device"
            width={720}
            height={960}
            className="w-80 xs:w-100 h-150 object-contain shrink-0"
          />
        </div>
        {order && !loading && !unavailable && (
          <div className="px-15 pb-20">
            <LabKitProgress order={order} inverse />
          </div>
        )}
      </HomeFeatureCard>
    </div>
  );
}
